import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { sellerLabel } from '~/utils';
import { offeredBundleQuantity, priceLabels } from './quoteDisplay';
import {
  COMPARISON_GRID_ROW_CLASS,
  SELLER_COLUMN_WIDTH_CLASS,
  STICKY_ATTRIBUTE_LABEL_CLASS,
  STICKY_ATTRIBUTE_LABEL_POSITION_CLASS,
} from './comparisonGridLayout';
import { quoteItemCheckboxId } from './splitAwardGridCells';
import { selectionLabelClass } from '~/components/ui/selectionCheckbox';
import { ProductLengthDetails } from './ProductLengthDetails';
import {
  SellerActionRow,
  type RenderQuoteActions,
} from './SellerQuoteActionSlots';
import { OfferedPrice } from './OfferedPrice';
import { SellerOfferNotice } from './SellerOfferNotice';
import { isOfferSelectable } from './sellerOffer';
import { unmaskPrimaryContact } from '~/api/sellerContacts';
import { SellerContacts } from './SellerContacts';
import { QuoteSellerName } from './QuoteSellerName';
import { QuoteReadyToShip } from './QuoteReadyToShip';
import { QuoteStatusLabel } from './QuoteStatusLabel';
import {
  SellerResponseInput,
  SellerResponseTerms,
  type SellerResponseField,
} from './SellerResponseFields';
import type { SellerResponseDrafts } from './useSellerResponseDrafts';
import type {
  ItemBlockData,
  ItemBlockGroup,
  Quote,
  QuoteItem,
} from './splitAwardGridTypes';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';

type ColumnProps = Readonly<{
  columnQuotes: ReadonlyArray<Quote>;
}>;

type SelectionProps = Readonly<{
  selectedItemIds: Set<string>;
  onToggleItem: (itemId: string) => void;
  disabled: boolean;
}>;

type SellerResponseProps = Readonly<{
  sellerResponses: SellerResponseDrafts;
}>;

function offerCell(
  item: QuoteItem | undefined,
  quote: Quote,
  renderCell: (item: QuoteItem, quote: Quote) => ReactNode
): ReactNode {
  return item == null ? '—' : renderCell(item, quote);
}

export function SellerColumnsTable({
  itemBlockGroups,
  columnQuotes,
  selectedItemIds,
  onToggleItem,
  disabled,
  renderQuoteActions,
  onBuyerResponded,
  sellerResponses,
}: Readonly<
  {
    itemBlockGroups: ReadonlyArray<ItemBlockGroup>;
    renderQuoteActions: RenderQuoteActions;
    onBuyerResponded: (quoteId: string) => void;
  } & ColumnProps &
    SelectionProps &
    SellerResponseProps
>) {
  return (
    <div className="overflow-x-auto rounded-lg border border-neut-border bg-neut-bg-alt p-4">
      <table role="table" className="block w-max text-sm">
        <SellerHeader quotes={columnQuotes} sellerResponses={sellerResponses} />
        {itemBlockGroups.flatMap(({ group, blocks }) =>
          blocks.map((block) => (
            <ItemRowGroup
              key={block.row.platformProductLengthId}
              group={group}
              block={block}
              columnQuotes={columnQuotes}
              selectedItemIds={selectedItemIds}
              onToggleItem={onToggleItem}
              disabled={disabled}
              sellerResponses={sellerResponses}
            />
          ))
        )}
        <tbody role="rowgroup" className="block">
          <SellerActionRow
            quotes={columnQuotes}
            renderQuoteActions={renderQuoteActions}
            onBuyerResponded={onBuyerResponded}
            cellPaddingClass="pt-3"
            sellerResponseOf={sellerResponses.responseOf}
          />
        </tbody>
      </table>
    </div>
  );
}

function SellerHeader({
  quotes,
  sellerResponses,
}: Readonly<{ quotes: ReadonlyArray<Quote> }> & SellerResponseProps) {
  return (
    <thead role="rowgroup" className="block border-b border-neut-border pb-2">
      <tr
        role="row"
        className={`${COMPARISON_GRID_ROW_CLASS} text-left text-neut-prim`}
      >
        <th
          role="columnheader"
          className={`${STICKY_ATTRIBUTE_LABEL_CLASS} font-normal`}
        />
        {quotes.map((quote) => {
          const response = sellerResponses.responseOf(quote);
          return (
            <th
              key={quote.id}
              role="columnheader"
              scope="col"
              className={`${SELLER_COLUMN_WIDTH_CLASS} font-normal`}
              data-testid={`seller-column-${quote.id}`}
            >
              <QuoteSellerName quote={quote} />
              <QuoteStatusLabel
                quote={quote}
                className="block text-xs text-neut-sec"
              />
              <SellerOfferNotice quote={quote} />
              <SellerContacts
                sellerName={sellerLabel(quote.sellingOrganization)}
                recipients={quote.recipients}
                primaryContactId={
                  unmaskPrimaryContact(quote.primaryContact)?.id
                }
              />
              <QuoteReadyToShip quote={quote} />
              {response && (
                <SellerResponseTerms quote={quote} response={response} />
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

function ItemRowGroup({
  group,
  block,
  columnQuotes,
  selectedItemIds,
  onToggleItem,
  disabled,
  sellerResponses,
}: Readonly<
  { group: ProductGroup; block: ItemBlockData } & ColumnProps &
    SelectionProps &
    SellerResponseProps
>) {
  const { t } = useTranslation('rfq-view');
  const { row, requestedBundles, lowestAmounts, offeringByQuoteId } = block;
  const { pickupPriceLabel, deliveryPriceLabel } = priceLabels(
    group.standardUnitsCode,
    t
  );
  const lengthOfRow = {
    productName: group.productName,
    length: row.displayValue,
  };
  const attributeRows: ReadonlyArray<{
    key: string;
    label: string;
    responseField: SellerResponseField;
    responseAriaLabel: (sellerName: string) => string;
    renderCell: (item: QuoteItem, quote: Quote) => ReactNode;
  }> = [
    {
      key: 'quantity',
      label: t('rfq_view.quantity_label'),
      responseField: 'quantityInBundles',
      responseAriaLabel: (sellerName) =>
        t('rfq_view.seller_bundles_aria_label', { sellerName, ...lengthOfRow }),
      renderCell: (item, quote) => {
        const bundleQuantity = offeredBundleQuantity(item, row);
        const isShort =
          requestedBundles != null && bundleQuantity < requestedBundles;
        const isSelectable = isOfferSelectable(quote, item, disabled);
        return (
          <span className="flex items-center gap-2">
            <label
              htmlFor={quoteItemCheckboxId(item.id)}
              className={selectionLabelClass(!isSelectable)}
            >
              <input
                id={quoteItemCheckboxId(item.id)}
                type="checkbox"
                checked={selectedItemIds.has(item.id)}
                disabled={!isSelectable}
                onChange={() => onToggleItem(item.id)}
                aria-label={t('rfq_view.select_seller_length_aria_label', {
                  sellerName: sellerLabel(quote.sellingOrganization),
                  ...lengthOfRow,
                })}
                data-testid={`quote-item-checkbox-${item.id}`}
              />
            </label>
            <span>
              {bundleQuantity}
              {isShort && (
                <span className="ml-1 text-xs text-warning-700">
                  {t('rfq_view.quantity_short_label', {
                    requested: requestedBundles,
                  })}
                </span>
              )}
            </span>
            {item.order != null && (
              <Link
                to={`/orders/view/${item.order.id}`}
                className="text-xs text-prim underline"
                data-testid={`ordered-link-${item.id}`}
              >
                {t('rfq_view.quote_item_ordered_label')}
              </Link>
            )}
          </span>
        );
      },
    },
    {
      key: 'pickupPrice',
      label: pickupPriceLabel,
      responseField: 'pickupPrice',
      responseAriaLabel: (sellerName) =>
        t('rfq_view.seller_pickup_price_aria_label', {
          sellerName,
          ...lengthOfRow,
        }),
      renderCell: (item) => (
        <OfferedPrice
          itemId={item.id}
          price={item.offeredPickupPrice}
          lowestAmount={lowestAmounts.pickup}
          kind="pickup"
        />
      ),
    },
    {
      key: 'deliveryPrice',
      label: deliveryPriceLabel,
      responseField: 'deliveryPrice',
      responseAriaLabel: (sellerName) =>
        t('rfq_view.seller_delivery_price_aria_label', {
          sellerName,
          ...lengthOfRow,
        }),
      renderCell: (item) => (
        <OfferedPrice
          itemId={item.id}
          price={item.offeredDeliveryPrice}
          lowestAmount={lowestAmounts.delivery}
          kind="delivery"
        />
      ),
    },
  ];

  return (
    <tbody
      role="rowgroup"
      className="block border-b border-neut-border py-3 last:border-b-0 last:pb-0"
      data-testid={`item-block-${row.platformProductLengthId}`}
    >
      <tr role="row" className="block">
        <th
          role="rowheader"
          scope="rowgroup"
          className={`${STICKY_ATTRIBUTE_LABEL_POSITION_CLASS} flex flex-col gap-0.5 bg-neut-bg-alt pb-2 text-left text-sm font-semibold text-neut-prim`}
        >
          <ProductLengthDetails
            group={group}
            row={row}
            requestedBundles={requestedBundles}
          />
        </th>
      </tr>
      {attributeRows.map((attributeRow) => (
        <tr
          key={attributeRow.key}
          role="row"
          className={COMPARISON_GRID_ROW_CLASS}
        >
          <th
            role="rowheader"
            scope="row"
            className={`${STICKY_ATTRIBUTE_LABEL_CLASS} py-1 text-left font-normal text-neut-sec`}
          >
            {attributeRow.label}
          </th>
          {columnQuotes.map((quote) => {
            const response = sellerResponses.responseOf(quote);
            return (
              <td
                key={quote.id}
                role="cell"
                className={`${SELLER_COLUMN_WIDTH_CLASS} py-1 text-neut-prim`}
                data-testid={`seller-${attributeRow.key}-${quote.id}-${row.platformProductLengthId}`}
              >
                {response ? (
                  <SellerResponseInput
                    quote={quote}
                    platformProductLengthId={row.platformProductLengthId}
                    field={attributeRow.responseField}
                    ariaLabel={attributeRow.responseAriaLabel(
                      sellerLabel(quote.sellingOrganization)
                    )}
                    response={response}
                  />
                ) : (
                  offerCell(
                    offeringByQuoteId.get(quote.id),
                    quote,
                    attributeRow.renderCell
                  )
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
}
